import { useBusinessContext } from '@context'
import { canFetchBusinessFeatures, IFeatureKey, IFeatureV2, IFlagV2, useFeatures } from '@query'
import React, { useContext, useMemo } from 'react'

type FeatureKey = string
type FlagKey = string

type ExtendedState<T> = T & {
  enabled: boolean
}

export type IFeatures = {
  [K in IFeatureKey]?: ExtendedState<IFeatureV2>
}

export type IFlags = {
  [key: FlagKey]: IFlagV2
}

interface IFeatureContextValue {
  features: IFeatures
  flags: IFlags
  flagsLoaded: boolean
}

const DEFAULT_FEATURE_CONTEXT_VALUE: IFeatureContextValue = {
  features: {},
  flags: {},
  flagsLoaded: false
}

const FeatureContext = React.createContext<IFeatureContextValue>(DEFAULT_FEATURE_CONTEXT_VALUE)

// Fallback for feature keys no longer returned by BE; keep FE resilient by defaulting to enabled.
const defaultFeatureState = (key: FeatureKey): ExtendedState<IFeatureV2> => ({
  key: key as IFeatureKey,
  enabled: true,
  blocked: false,
  hidden: false
})

// Fallback for missing flags for the same reason: removed BE keys should not break FE rendering.
const defaultFlagState = (key: FlagKey): IFlagV2 => ({
  key,
  enabled: true
})

const mapFeaturesToState = (features: IFeatureV2[] = []): IFeatures => {
  const states: IFeatures = {}

  for (const feature of features) {
    states[feature.key] = {
      key: feature.key,
      hidden: feature.hidden,
      blocked: feature.blocked,
      enabled: !feature.hidden && !feature.blocked
    }
  }

  return states
}

const mapFlagsToState = (flags: IFlagV2[] = []): IFlags => {
  const states: IFlags = {}

  for (const flag of flags) {
    states[flag.key] = {
      key: flag.key,
      enabled: flag.enabled
    }
  }

  return states
}

export const useFeatureContext = (): IFeatureContextValue => useContext(FeatureContext)

export const FeatureContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { slug, data: business } = useBusinessContext()
  const { data, isSuccess, isError } = useFeatures({ slug })

  // Failed, skipped (endpoint closed), and success all count as answered.
  const flagsLoaded =
    isSuccess || isError || (business != null && !canFetchBusinessFeatures(business))

  return (
    <FeatureContext.Provider
      value={{
        features: useMemo(() => mapFeaturesToState(data?.features), [data?.features]),
        flags: useMemo(() => mapFlagsToState(data?.flags), [data?.flags]),
        flagsLoaded
      }}
    >
      {children}
    </FeatureContext.Provider>
  )
}

interface FeatureWrapperProps {
  feature: FeatureKey
  alternativeContent?: React.ReactNode
  children: React.ReactNode
}

interface FlagProps {
  flag: FlagKey
  alternativeContent?: () => React.ReactNode
  children: React.ReactNode
}

export const useFeature = (key: FeatureKey): ExtendedState<IFeatureV2> => {
  const context = useFeatureContext()
  return context.features[key] ?? defaultFeatureState(key)
}

export const useFlag = (key: FlagKey): IFlagV2 => {
  const context = useFeatureContext()
  return context.flags[key] ?? defaultFlagState(key)
}

/** True once flags are known. Unloaded flags read as enabled, so new UI must wait. */
export const useFlagsLoaded = (): boolean => useFeatureContext().flagsLoaded

export const FeatureWrapper: React.FC<FeatureWrapperProps> = ({
  feature: featureKey,
  children,
  alternativeContent
}) => {
  const state = useFeature(featureKey)

  if (state.enabled) return children
  if (state.hidden) return null
  if (state.blocked) return alternativeContent ? <>{alternativeContent}</> : null

  return null
}

export const FlagWrapper: React.FC<FlagProps> = ({
  flag: flagKey,
  alternativeContent = () => null,
  children
}) => {
  const flagsLoaded = useFlagsLoaded()
  const flag = useFlag(flagKey)

  if (!flagsLoaded) {
    return <>{alternativeContent()}</>
  }

  return flag?.enabled ? <>{children}</> : <>{alternativeContent()}</>
}
